// Unpublished Work © 2022-2026 Deere & Company.

import React from 'react';
import {connect} from 'react-redux';
import FormValidator from 'Ui/components/higher-order-components/form-validator';
import PropTypes from 'Utils/prop-type-utils';
import {closeDialog, openDialog} from 'Store/actions/dialogs';
import dialogTypes from 'Ui/components/common/dialog-types';
import moment from 'moment';
import {useSave} from 'Ui/react-hooks/use-save';
import {
    changeEmployeeStatus,
    combineConsecutiveEvents,
    deleteEmployeeStatus,
    getAddEditFooterActions
} from 'OnLabor/workboard/utils/time-off-utils';
import {useToast} from 'Src/ui-core/react-hooks/use-toast';
import {isNullOrUndefined} from 'Common/utils/validation-utils';
import {Dialog} from '@deere/isg.component-library';
import {getEmployeeStatusOptions, getTimeOff} from 'Services/membership-service';
import {fetchEffectData} from 'Utils/react-utils';
import {formatLocalizedTime} from 'Utils/time-utils';
import LoadingWrapper from 'Ui/components/common/loading-wrapper';
import TimeOffInputs from 'Ui/components/common/time-off-inputs';
import {replaceTranslationNames} from 'Utils/translation-utils';
import {TOAST_TYPE} from 'Src/ui-core/constants/toast-constants';
import {CONFLICT} from 'Common/constants/errors';
import {fetchAndFilterInactiveOperators} from 'OnLabor/workboard/utils/fetch-operators';
import DataGridWrapper from 'Src/ui-core/components/common/data-table/data-grid-wrapper';

const SUCCESS_CODE = 200;

function getOptionsMultiselectItems(employeeStatusOptions) {
    return employeeStatusOptions?.map((data) => (

        {
            ...data,
            title: data.name,
            id: data.employeeStatusOptionId
        }
    ));
}

function getUsersMultiselectItems(user) {
    const uniqueUsersById = new Map();

    user?.forEach((data) => {
        if (!uniqueUsersById.has(data.id)) {
            uniqueUsersById.set(data.id, {
                ...data,
                title: `${data.firstName} ${data.lastName}`,
                id: data.id
            });
        }
    });

    return [...uniqueUsersById.values()];
}

function getTableData(availabilityData) {
    const restructuredEvents = combineConsecutiveEvents(availabilityData, false);

    return restructuredEvents?.map((data) => (
        {
            name: `${data.firstName} ${data.lastName}`,
            from: formatLocalizedTime(moment(data.start).toDate(), {
                month: 'short',
                day: 'numeric',
                year: 'numeric'
            }),
            to: formatLocalizedTime(moment(data.end).toDate(), {
                month: 'short',
                day: 'numeric',
                year: 'numeric'
            }),
            leaveType: data.employeeStatusOption
        }
    ));
}

function inititializeState(timeOffValues) {
    const [availabilityData, setAvailabilityData] = React.useState([]);
    const [loading, setLoading] = React.useState(true);
    const [values, setValues] = React.useState({
        name: timeOffValues?.appUserId,
        leaveType: timeOffValues?.employeeStatusOptionId,
        fromDate: timeOffValues?.dateSelect ? moment(timeOffValues.dateSelect) : moment(),
        toDate: timeOffValues?.toDate ? moment(timeOffValues.toDate) : moment()
    });

    const statusOptionsRef = React.useRef([]);
    const usersRef = React.useRef([]);

    return {
        availabilityData,
        setAvailabilityData,
        loading,
        setLoading,
        values,
        setValues,
        statusOptionsRef,
        usersRef
    };
}

function getDataGridColumns(translations) {
    return [
        {
            headerName: translations.NAME,
            flex: 1,
            minWidth: 150,
            field: 'name'
        },
        {
            headerName: translations.ONLINK_FROM,
            flex: 1,
            minWidth: 100,
            field: 'from'
        },
        {
            headerName: translations.ONLINK_TO,
            flex: 1,
            minWidth: 100,
            field: 'to'
        },
        {
            headerName: translations.ONLINK_LEAVE_TYPE,
            flex: 1,
            minWidth: 150,
            field: 'leaveType'
        }
    ];
}

function getRowId(row) {
    return row.name + row.from + row.to;
}

function AddTimeOffDialog(props) {
    const {
        closeAddTimeOffDialog,
        closeConfirmation,
        openConfirmation,
        invalidInputs,
        onClose,
        setValid,
        translations,
        timeOffValues,
        orgId,
        membershipId
    } = props;

    const {
        availabilityData,
        setAvailabilityData,
        loading,
        setLoading,
        values,
        setValues,
        statusOptionsRef,
        usersRef
    } = inititializeState(timeOffValues);

    const {addToast} = useToast();

    function onChange(name, value) {
        setValues((prevValues) => ({
            ...prevValues,
            [name]: value
        }));
    }

    async function fetchData(isMounted) {
        setLoading(true);
        const [{employeeStatus}, {employeeStatusOptions}, users] = await Promise.all([
            !timeOffValues ? getTimeOff({
                startDate: moment().format('YYYYMMDD')
            }) : Promise.resolve({}),
            getEmployeeStatusOptions(),
            fetchAndFilterInactiveOperators(orgId)
        ]);

        const unavailableTimeOffEvents = employeeStatus?.filter((event) => event.isAvailable === false);

        if (isMounted()) {
            setAvailabilityData(getTableData(unavailableTimeOffEvents));
            statusOptionsRef.current = getOptionsMultiselectItems(employeeStatusOptions);
            usersRef.current = getUsersMultiselectItems(users);
            setLoading(false);
        }
    }

    const [saveFunc, disableSave, isSaving] = useSave(async () => {
        setLoading(true);
        let clearResult = SUCCESS_CODE;

        const userData = usersRef.current.find((user) => user.id === values.name);

        if (timeOffValues) {
            clearResult = await deleteEmployeeStatus({
                appUserId: timeOffValues.appUserId,
                employeeStatusOptionId: timeOffValues.employeeStatusOptionId
            }, timeOffValues);
        }

        if (clearResult !== CONFLICT) {
            await changeEmployeeStatus({
                appUserId: values.name,
                firstName: userData?.firstName || '',
                lastName: userData?.lastName || '',
                membershipId,
                employeeStatusOptionId: values.leaveType
            }, values);
        }

        if (onClose) {
            onClose();
        }

        setLoading(false);
        closeAddTimeOffDialog();
    }, {
        disabled: loading,
        invalidInputs
    });

    async function deleteHandler() {
        openConfirmation({
            message: replaceTranslationNames(translations.ONLINK_ITEM_THIS_CANNOT_BE_UNDONE, {
                '0': translations.ONLINK_TIME_OFF.toLowerCase()
            }),
            title: replaceTranslationNames(translations.ONLINK_DELETE_ITEM, {
                '0': translations.ONLINK_TIME_OFF
            }),
            async onContinue() {
                closeConfirmation();
                setLoading(true);

                const deleteStatus = await deleteEmployeeStatus({
                    appUserId: timeOffValues.appUserId,
                    employeeStatusOptionId: timeOffValues.employeeStatusOptionId
                }, timeOffValues);

                if (deleteStatus !== null && deleteStatus !== CONFLICT) {
                    setLoading(false);
                    addToast({
                        message: replaceTranslationNames(translations.ONLINK_CONFIRM_ITEM_DELETION_FAILED, {
                            '0': translations.ONLINK_TIME_OFF.toLowerCase()
                        }),
                        type: TOAST_TYPE.ERROR
                    });

                    return;
                }

                if (onClose) {
                    onClose();
                }
                setLoading(false);
                closeAddTimeOffDialog();
            },
            onCancel: closeConfirmation
        });
    }

    React.useEffect(() => fetchEffectData(fetchData), []);

    const title = timeOffValues ? translations.ONLINK_EDIT_TIME_OFF : translations.ONLINK_ADD_TIME_OFF;

    const footerMessage = `* = ${translations.REQUIRED}`;

    const dataComponent = (
        <DataGridWrapper
            columns={getDataGridColumns(translations)}
            getRowId={getRowId}
            headerComponent={translations.ONLINK_UPCOMING_TIME_OFF}
            rows={availabilityData}
            slotProps={{
                root: {
                    disableColumnMenu: true
                }
            }}
            translations={translations}
        />
    );

    return (
        <Dialog
            className={'add-time-off-dialog dropdown-overflow-dialog'}
            closeHandler={closeAddTimeOffDialog}
            footerActions={getAddEditFooterActions(!isNullOrUndefined(timeOffValues), translations, closeAddTimeOffDialog, saveFunc, addToast, disableSave, deleteHandler)}
            footerMessage={footerMessage}
            open={true}
            title={title}
        >
            <LoadingWrapper
                className='onlink-loading-icon'
                loading={loading || isSaving}
                size='50px'
            >
                <div className='add-todo-form'>
                    <TimeOffInputs
                        onChange={onChange}
                        setValid={setValid}
                        statusOptionsRef={statusOptionsRef}
                        translations={translations}
                        usersRef={usersRef}
                        values={values}
                    />
                    {!isNullOrUndefined(timeOffValues) || dataComponent}
                </div>
            </LoadingWrapper>
        </Dialog>
    );
}

AddTimeOffDialog.propTypes = {
    closeAddTimeOffDialog: PropTypes.func,
    closeConfirmation: PropTypes.func,
    invalidInputs: PropTypes.instanceOf(Set),
    membershipId: PropTypes.string,
    onClose: PropTypes.func,
    openConfirmation: PropTypes.func,
    orgId: PropTypes.number,
    setValid: PropTypes.func,
    timeOffValues: PropTypes.object,
    translations: PropTypes.object
};

export function mapStateToProps(state) {
    return {
        translations: state.translations,
        orgId: state.membership.orgId,
        membershipId: state.membership.membershipId
    };
}

export function mapDispatchToProps(dispatch) {
    return {
        closeAddTimeOffDialog() {
            dispatch(closeDialog(dialogTypes.ADD_TIME_OFF_DIALOG));
        },
        closeConfirmation() {
            dispatch(closeDialog(dialogTypes.CONFIRMATION_DIALOG));
        },
        openConfirmation(props) {
            dispatch(openDialog(dialogTypes.CONFIRMATION_DIALOG, props));
        }
    };
}

export default connect(mapStateToProps, mapDispatchToProps)(FormValidator(AddTimeOffDialog));
